import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Phone, MessageCircle, ChevronRight, Package, Settings, Gauge, Truck } from 'lucide-react';
import AnimatedSection from '../components/common/AnimatedSection';
import SEOHead from '../components/common/SEOHead';
import { products, companyInfo } from '../data/mock';

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const product = products.find(p => p.id === parseInt(id));

  if (!product) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Produto não encontrado</h1>
          <p className="text-gray-600 mb-8">O produto que você está procurando não existe.</p>
          <Link
            to="/produtos"
            className="inline-flex items-center gap-2 bg-teal-500 text-white px-6 py-3 rounded-xl font-semibold hover:bg-teal-600 transition-colors"
          >
            <ArrowLeft size={20} />
            Ver todos os produtos
          </Link>
        </div>
      </div>
    );
  }

  const relatedProducts = products
    .filter(p => (p.brand === product.brand || p.fuelType === product.fuelType) && p.id !== product.id)
    .slice(0, 3);

  const getFuelTypeLabel = (fuelType) => {
    const labels = {
      diesel: 'Diesel',
      glp: 'GLP',
      eletrica: 'Elétrica',
      manual: 'Manual'
    };
    return labels[fuelType] || fuelType || 'N/A';
  };

  return (
    <>
      <SEOHead 
        title={`${product.name} - ${product.model}`}
        description={`${product.description} Capacidade: ${product.capacity}. Marca: ${product.brand}. Solicite um orçamento!`}
        keywords={`${product.name}, ${product.model}, ${product.brand}, empilhadeira ${getFuelTypeLabel(product.fuelType)}, ${product.capacity}`}
      />

      {/* Breadcrumb */}
      <section className="bg-gray-100 py-4">
        <div className="container mx-auto px-4">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-teal-600 transition-colors">Home</Link>
            <ChevronRight size={14} className="text-gray-400" />
            <Link to="/produtos" className="text-gray-500 hover:text-teal-600 transition-colors">Produtos</Link>
            <ChevronRight size={14} className="text-gray-400" />
            <span className="text-teal-600 font-medium">{product.name}</span>
          </nav>
        </div>
      </section>

      {/* Product Detail */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-teal-600 transition-colors mb-8"
          >
            <ArrowLeft size={20} />
            Voltar
          </button>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <AnimatedSection animation="fadeRight">
              <div className="relative">
                {/* Main Image Container with Gray Background */}
                <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-3xl p-8 shadow-lg">
                  <div className="aspect-square bg-white rounded-2xl overflow-hidden flex items-center justify-center p-4">
                    <img 
                      src={product.imageIsolated || product.image} 
                      alt={product.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                </div>
                
                {/* Brand Badge */}
                <div className="absolute top-4 left-4 bg-teal-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                  {product.brand}
                </div>
                
                {/* Fuel Type Badge */}
                {product.fuelType && (
                  <div className={`absolute top-4 right-4 px-4 py-2 rounded-full text-sm font-medium ${
                    product.fuelType === 'diesel' ? 'bg-orange-500 text-white' :
                    product.fuelType === 'glp' ? 'bg-blue-500 text-white' :
                    product.fuelType === 'eletrica' ? 'bg-green-500 text-white' :
                    'bg-gray-900 text-white'
                  }`}>
                    {getFuelTypeLabel(product.fuelType)}
                  </div>
                )}
              </div>
            </AnimatedSection>

            {/* Product Info */}
            <AnimatedSection animation="fadeLeft">
              <div className="space-y-6">
                {/* Title */}
                <div>
                  <p className="text-teal-600 font-semibold mb-2">Modelo: {product.model}</p>
                  <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                    {product.name}
                  </h1>
                  <p className="text-xl text-gray-600 leading-relaxed">
                    {product.description}
                  </p>
                </div>

                {/* Capacity Highlight */}
                <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center">
                      <Gauge size={28} className="text-teal-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Capacidade de Carga</p>
                      <p className="text-2xl font-bold text-teal-600">{product.capacity}</p>
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div>
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <CheckCircle size={20} className="text-teal-500" />
                    Características
                  </h3>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {product.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2 text-gray-600">
                        <span className="w-1.5 h-1.5 bg-teal-500 rounded-full mt-2 flex-shrink-0"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-wrap gap-4 pt-4">
                  <a
                    href={`https://wa.me/${companyInfo.whatsapp}?text=Olá! Gostaria de um orçamento para: ${product.name} (${product.model})`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-xl font-semibold shadow-lg hover:shadow-green-500/30 hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <MessageCircle size={20} />
                    Solicitar Orçamento
                  </a>
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="bg-gray-100 text-gray-900 px-6 py-4 rounded-xl font-semibold hover:bg-gray-200 transition-all duration-300 flex items-center justify-center gap-2"
                  >
                    <Phone size={20} />
                    Ligar Agora
                  </a>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Specifications */}
      {product.specifications && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fadeUp">
              <h2 className="text-3xl font-bold text-gray-900 mb-8 flex items-center gap-3">
                <Settings size={28} className="text-teal-500" />
                Especificações Técnicas
              </h2>
              
              <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="grid md:grid-cols-2">
                  {Object.entries(product.specifications).map(([key, value], index) => (
                    <div 
                      key={key} 
                      className={`flex items-center justify-between p-5 ${
                        index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                      } ${index < Object.keys(product.specifications).length - 2 ? 'border-b border-gray-100' : ''}`}
                    >
                      <span className="text-gray-600 font-medium">{key}</span>
                      <span className="text-gray-900 font-semibold">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      )}

      {/* Services */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Serviços Disponíveis</h2>
            <p className="text-gray-600">Este equipamento está disponível para:</p>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Venda', description: 'Adquira este equipamento novo ou usado.', icon: Package },
              { title: 'Locação', description: 'Aluguel diário, mensal ou anual.', icon: Truck },
              { title: 'Manutenção', description: 'Serviço técnico especializado.', icon: Settings }
            ].map((service, index) => (
              <AnimatedSection key={service.title} animation="fadeUp" delay={index * 100}>
                <div className="p-6 bg-gray-50 rounded-2xl text-center hover:bg-teal-50 hover:border-teal-200 border border-transparent transition-all duration-300">
                  <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <service.icon size={24} className="text-teal-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className="text-gray-600 text-sm">{service.description}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <AnimatedSection animation="fadeUp" className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Produtos Relacionados</h2>
              <p className="text-gray-600">Veja outros equipamentos que podem atender sua necessidade</p>
            </AnimatedSection>

            <div className="grid md:grid-cols-3 gap-6">
              {relatedProducts.map((relProduct, index) => (
                <AnimatedSection key={relProduct.id} animation="fadeUp" delay={index * 100}>
                  <Link 
                    to={`/produtos/${relProduct.id}`}
                    className="group block bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl border border-gray-100 hover:border-teal-200 transition-all duration-500"
                  >
                    <div className="relative">
                      <div className="bg-gray-100 p-4">
                        <img 
                          src={relProduct.image} 
                          alt={relProduct.name}
                          className="w-full h-48 object-contain group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="absolute top-4 left-4">
                        <span className="bg-teal-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                          {relProduct.brand}
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="font-bold text-gray-900 mb-2 group-hover:text-teal-600 transition-colors">
                        {relProduct.name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-3">Modelo: {relProduct.model}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-teal-600 font-semibold">{relProduct.capacity}</span>
                        <span className="text-teal-500 flex items-center gap-1 text-sm font-medium group-hover:translate-x-1 transition-transform">
                          Ver detalhes <ChevronRight size={16} />
                        </span>
                      </div>
                    </div>
                  </Link>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-teal-600">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Interessado neste equipamento?
            </h2>
            <p className="text-xl text-teal-100 mb-8">
              Entre em contato e solicite um orçamento personalizado
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={`https://wa.me/${companyInfo.whatsapp}?text=Olá! Gostaria de um orçamento para: ${product.name} (${product.model})`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-teal-600 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <MessageCircle size={20} />
                WhatsApp
              </a>
              <Link
                to="/contato"
                className="bg-teal-700 text-white px-8 py-4 rounded-xl font-bold hover:bg-teal-800 hover:scale-105 transition-all duration-300"
              >
                Formulário de Contato
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default ProductDetailPage;
